/**
 * Icon mapping utility for MetadataInspector
 *
 * Maps Ant Design icon name strings to actual icon components.
 * This is needed because metadata returns icon names as strings.
 */

import React from 'react'
import {
  ShoppingOutlined,
  UserOutlined,
  TeamOutlined,
  SettingOutlined,
  FileOutlined,
  DatabaseOutlined,
  ApiOutlined,
  DollarOutlined,
  SwapOutlined,
  BellOutlined,
  HistoryOutlined,
  LockOutlined,
  AppstoreOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  EditOutlined,
  QuestionOutlined,
  HomeOutlined,
  FolderOutlined,
  TagOutlined,
  StarOutlined,
  HeartOutlined,
  ToolOutlined,
  CloudOutlined,
  LinkOutlined,
  GlobalOutlined,
  MailOutlined,
  PhoneOutlined,
  CalendarOutlined,
  PictureOutlined,
  VideoCameraOutlined,
  SoundOutlined,
  CarOutlined,
  RocketOutlined,
  ThunderboltOutlined,
  FireOutlined,
  CrownOutlined,
  TrophyOutlined,
  GiftOutlined,
  ShopOutlined,
  BankOutlined,
  CreditCardOutlined,
  WalletOutlined,
  SafetyOutlined,
  AuditOutlined,
  SolutionOutlined,
  ContainerOutlined,
  InboxOutlined,
  ProfileOutlined,
  ReconciliationOutlined,
  InsuranceOutlined,
  InteractionOutlined,
  NodeIndexOutlined
} from '@ant-design/icons'

/**
 * Map of icon name strings to React components
 */
const ICON_MAP: Record<string, React.ReactNode> = {
  ShoppingOutlined: <ShoppingOutlined />,
  UserOutlined: <UserOutlined />,
  TeamOutlined: <TeamOutlined />,
  SettingOutlined: <SettingOutlined />,
  FileOutlined: <FileOutlined />,
  DatabaseOutlined: <DatabaseOutlined />,
  ApiOutlined: <ApiOutlined />,
  DollarOutlined: <DollarOutlined />,
  SwapOutlined: <SwapOutlined />,
  BellOutlined: <BellOutlined />,
  HistoryOutlined: <HistoryOutlined />,
  LockOutlined: <LockOutlined />,
  AppstoreOutlined: <AppstoreOutlined />,
  CheckCircleOutlined: <CheckCircleOutlined />,
  ClockCircleOutlined: <ClockCircleOutlined />,
  EditOutlined: <EditOutlined />,
  QuestionOutlined: <QuestionOutlined />,
  HomeOutlined: <HomeOutlined />,
  FolderOutlined: <FolderOutlined />,
  TagOutlined: <TagOutlined />,
  StarOutlined: <StarOutlined />,
  HeartOutlined: <HeartOutlined />,
  ToolOutlined: <ToolOutlined />,
  CloudOutlined: <CloudOutlined />,
  LinkOutlined: <LinkOutlined />,
  GlobalOutlined: <GlobalOutlined />,
  MailOutlined: <MailOutlined />,
  PhoneOutlined: <PhoneOutlined />,
  CalendarOutlined: <CalendarOutlined />,
  PictureOutlined: <PictureOutlined />,
  VideoCameraOutlined: <VideoCameraOutlined />,
  SoundOutlined: <SoundOutlined />,
  CarOutlined: <CarOutlined />,
  RocketOutlined: <RocketOutlined />,
  ThunderboltOutlined: <ThunderboltOutlined />,
  FireOutlined: <FireOutlined />,
  CrownOutlined: <CrownOutlined />,
  TrophyOutlined: <TrophyOutlined />,
  GiftOutlined: <GiftOutlined />,
  ShopOutlined: <ShopOutlined />,
  BankOutlined: <BankOutlined />,
  CreditCardOutlined: <CreditCardOutlined />,
  WalletOutlined: <WalletOutlined />,
  SafetyOutlined: <SafetyOutlined />,
  AuditOutlined: <AuditOutlined />,
  SolutionOutlined: <SolutionOutlined />,
  ContainerOutlined: <ContainerOutlined />,
  InboxOutlined: <InboxOutlined />,
  ProfileOutlined: <ProfileOutlined />,
  ReconciliationOutlined: <ReconciliationOutlined />,
  InsuranceOutlined: <InsuranceOutlined />,
  InteractionOutlined: <InteractionOutlined />,
  NodeIndexOutlined: <NodeIndexOutlined />
}

/**
 * Get icon component from icon name string
 *
 * @param iconName - Ant Design icon name (e.g., "ShoppingOutlined")
 * @param fallback - Fallback icon if not found (default: AppstoreOutlined)
 * @returns React icon component
 */
export function getIconFromName(
  iconName?: string,
  fallback: React.ReactNode = <AppstoreOutlined />
): React.ReactNode {
  if (!iconName) {
    return fallback
  }

  return ICON_MAP[iconName] || fallback
}

/**
 * Check if icon name exists in the map
 */
export function isValidIconName(iconName: string): boolean {
  return iconName in ICON_MAP
}
